import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useParams } from 'react-router-dom'
import {
  ColumnDef,
  IdentifiableModel,
  PaginationOptions,
  Table,
} from 'cs-kevlar-react'
import { useShards, useDynamicPageTitle } from 'hooks'
import {
  getCustomerProductLineLabel,
  getConnectorTypeLabel,
  hasOneOfPermissions,
} from 'utils'
import { SupportUserPermission } from '../../api'
import { CustomerAgencyShardMetadata, ShardMetadata } from 'api'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'customer',
  pluralAdjective: 'customers',
}

type ValidMenuKey = 'Details'

type ShardDetailsProps = {
  shardId: string
}

export type IEnumDescriptions = {
  productLineDescription: string
  connectorTypeDescription: string
}

export type ShardMetadataEx = ShardMetadata & IdentifiableModel
export type CustomerAgencyShardMetadataEx = CustomerAgencyShardMetadata &
  IdentifiableModel &
  IEnumDescriptions

const rootRoute = '/databases/:shardId'

const ShardDetails: React.FunctionComponent = () => {
  
  const params = useParams<ShardDetailsProps>()
  if(!params || !params.shardId) return null

  const { initialize, shards } = useShards()
  const [isLoading, setIsLoading] = React.useState(false)
  const shardId = params.shardId
  const { dispatch: dispatchPageTitle } = useDynamicPageTitle()

  const pageTitle = `Details for Database ${shardId}`
  usePageViewAudit(pageTitle, true, true)

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await initialize()
      setIsLoading(false)
    }

    loadData()

    dispatchPageTitle({
      type: 'SetPageTitle',
      payload: pageTitle,
    })

    return () => {
      dispatchPageTitle({
        type: 'ClearPageTitle',
      })
    }
  }, [])

  const columns: ColumnDef<
    CustomerAgencyShardMetadataEx,
    ValidMenuKey
  >[] = React.useMemo(
    () => [
      {
        label: 'Customer',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) => row.name,
        dataProperty: 'name' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Product Line',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) =>
          row.productLineDescription,
        dataProperty: 'productLineDescription' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Connector Type',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) =>
          row.connectorTypeDescription,
        dataProperty: 'connectorTypeDescription' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Licensed Users',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) =>
          row.totalLicensedUsers,
        dataProperty: 'totalLicensedUsers' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Connected Devices',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) =>
          row.totalConnectedDevices,
        dataProperty: 'totalConnectedDevices' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Connected Interfaces',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerAgencyShardMetadataEx) =>
          row.totalConnectedInterfaces,
        dataProperty: 'totalConnectedInterfaces' as keyof CustomerAgencyShardMetadataEx,
        filterable: true,
      },
    ],
    [],
  )

  const tableData: CustomerAgencyShardMetadataEx[] = React.useMemo(
    () =>
      (shards
        .find((p) => p.shardId == shardId)
        ?.agencies?.map((x) => ({
          ...x,
          id: x.customerAgencyId.toString(),
          productLineDescription: getCustomerProductLineLabel(x.productLine),
          connectorTypeDescription: getConnectorTypeLabel(x.connectorType),
        })) as CustomerAgencyShardMetadataEx[]) ?? [],
    [shards.find((p) => p.shardId == shardId)?.agencies],
  )

  return (
    <React.Fragment>
      <Table
        ariaLabel="Database Details"
        uniqueIdentifier="ShardCustomerMetadata"
        paginationOptions={PAGINATION_OPTIONS}
        useDefaultRow
        isLoading={isLoading}
        columns={
          columns as ColumnDef<CustomerAgencyShardMetadataEx, ValidMenuKey>[]
        }
        data={tableData}
      />
    </React.Fragment>
  )
}

export const ShardDetailsPage: PageDefinition = {
  routes: () => [rootRoute],
  routeBack: (user) => '/databases',
  type: [PageType.Nested],
  sortOrder: () => 0,
  component: <ShardDetails />,
  navLinkText: 'Database Details',
  pageTitle: (user) => {
    return 'Database Details'
  },
  canAccess: (user) => {
    return hasOneOfPermissions(user, [SupportUserPermission.ViewShards])
  },
  buildNavLink: (user) => {
    return rootRoute
  },
}
